'use client';

import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import downArrow from '../../assets/images/down_arrows.svg';
import passportDetailed from '../../assets/images/passport.jpg';
import process1 from '../../assets/images/process_icones1.svg';
import process2 from '../../assets/images/process_icones_steps2.svg';
import process3 from '../../assets/images/process_icones_steps3.svg';
import process4 from '../../assets/images/process_icon_steps4.svg';
import process5 from '../../assets/images/process_icons_steps5.svg';
import process6 from '../../assets/images/process_icon_steps6.svg';
import process7 from '../../assets/images/process_icon_steps7.svg';
import process8 from '../../assets/images/process_steps_icon8.svg';
import process9 from '../../assets/images/process_steps_icon9.svg';
import { fetchServices } from '../api';
import MetaUpdater from '../metadata/MetaUpdater';

// Define types for API data
interface ServiceData {
  _id: string;
  title: { en: string; ar: string };
  subtitles: Array<{ en: string; ar: string; _id: string }>;
  description: { en: string; ar: string };
  image?: string;
  createdAt: string;
  updatedAt: string;
  __v: number;
}

// Define the type for step content (unchanged)
interface StepContent {
  title: string;
  description1: string;
  description2?: string;
  description3?: string;
  description4?: string;
}

const Service: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [activeTab, setActiveTab] = useState<string>(''); // No default static tab
  const [activeStep, setActiveStep] = useState<number>(1);
  const [services, setServices] = useState<ServiceData[]>([]);
  const [selectedSubtitle, setSelectedSubtitle] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Get the current language, defaulting to 'en' if undefined
  const currentLanguage = (i18n.language || 'en') as 'en' | 'ar';

  // Fetch services data on component mount
  useEffect(() => {
    const loadServices = async () => {
      try {
        const data = await fetchServices();
        setServices(data);
        // Set default subtitle for the first tab if data exists
        if (
          data.length > 0 &&
          data[0].subtitles.length > 0 &&
          data[0].subtitles[0][currentLanguage]
        ) {
          setSelectedSubtitle(data[0].subtitles[0][currentLanguage]);
          setActiveTab(`pills-${data[0].title.en.toLowerCase()}`);
        } else {
          setError(
            t('errors.no_subtitles', {
              defaultValue: 'No subtitles available for the selected service',
            })
          );
        }
        setLoading(false);
      } catch (err) {
        setError(
          t('errors.fetch_failed', { defaultValue: 'Failed to load services' })
        );
        setLoading(false);
      }
    };
    loadServices();
  }, [t, currentLanguage]);

  const handleTabClick = (tabId: string) => {
    setActiveTab(tabId);
    // Reset subtitle when switching tabs
    const selectedService = services.find(
      (service) =>
        service.title.en.toLowerCase() === tabId.replace('pills-', '')
    );
    if (
      selectedService &&
      selectedService.subtitles.length > 0 &&
      selectedService.subtitles[0][currentLanguage]
    ) {
      setSelectedSubtitle(selectedService.subtitles[0][currentLanguage]);
    } else {
      setSelectedSubtitle(null);
    }
  };

  const handleSubtitleClick = (subtitle: string) => {
    setSelectedSubtitle(subtitle);
    // Find the service associated with the subtitle and set the active tab
    const selectedService = services.find((service) =>
      service.subtitles.some((sub) => sub[currentLanguage] === subtitle)
    );
    if (selectedService) {
      setActiveTab(`pills-${selectedService.title.en.toLowerCase()}`);
      // Close the dropdown by removing focus and triggering collapse
      const activeDropdown = document.querySelector(`.nav-item.show`);
      if (activeDropdown) {
        activeDropdown.classList.remove('show');
        const dropdownMenu = activeDropdown.querySelector('.sub-menu');
        if (dropdownMenu) {
          dropdownMenu.classList.remove('show');
        }
      }
    }
  };

  const handleStepClick = (step: number) => {
    setActiveStep(step);
  };

  // Memoize selectedService to optimize performance
  const selectedService = useMemo(
    () =>
      services.find((service) =>
        service.subtitles.some(
          (sub) => sub[currentLanguage] === selectedSubtitle
        )
      ),
    [services, selectedSubtitle, currentLanguage]
  );

  return (
    <>
      <MetaUpdater page="services" />

      {/* Hero Section */}
      <section className="service_heros">
        <div className="container">
          <div className="row">
            <div className="col-md-12 text-center">
              <div className="main-title">
                <h2>{t('service.title', { defaultValue: 'Our Services' })}</h2>
                <p>
                  {t('service.subtitle', {
                    defaultValue: 'Explore our wide range of services',
                  })}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="services">
        <div className="container">
          <div className="row">
            <div className="col-md-12">
              <div className="main-heading">
                <h2>
                  {t('service.heading', { defaultValue: 'Service Categories' })}
                </h2>
              </div>
            </div>

            <div className="col-md-12 pt-5">
              <ul
                className="nav nav-pills mb-3 d-flex  g-30"
                id="pills-tab"
                role="tablist"
              >
                {services.map((service) => (
                  <li
                    key={service._id}
                    className="nav-item"
                    role="presentation"
                  >
                    <button
                      className={`nav-link ${activeTab === `pills-${service.title.en.toLowerCase()}` ? 'active' : ''}`}
                      id={`pills-${service.title.en.toLowerCase()}-tab`}
                      onClick={() =>
                        handleTabClick(
                          `pills-${service.title.en.toLowerCase()}`
                        )
                      }
                      type="button"
                      role="tab"
                      aria-controls={`pills-${service.title.en.toLowerCase()}`}
                      aria-selected={
                        activeTab === `pills-${service.title.en.toLowerCase()}`
                      }
                    >
                      {service.title[currentLanguage] || service.title.en}
                    </button>
                    <ul className="sub-menu" role="menu">
                      {service.subtitles.map((subtitle, index) => (
                        <li
                          key={subtitle._id || index}
                          className="sub-menu-item"
                          role="none"
                        >
                          <a
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              handleSubtitleClick(subtitle[currentLanguage]);
                            }}
                            className={
                              selectedSubtitle === subtitle[currentLanguage]
                                ? 'active'
                                : ''
                            }
                            role="menuitem"
                            aria-current={
                              selectedSubtitle === subtitle[currentLanguage]
                                ? 'true'
                                : 'false'
                            }
                          >
                            {subtitle[currentLanguage] || subtitle.en}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </li>
                ))}
              </ul>
            </div>

            <div className="col-md-12">
              <div
                className=""
                style={{
                  boxShadow: '0px 0px 20px 20px #00000033',
                  borderRadius: '25px',
                }}
              >
                <div className="tab-content" id="pills-tabContent">
                  {loading ? (
                    <div className="text-center">
                      <div className="spinner-border" role="status">
                        <span className="visually-hidden">
                          {t('loading', { defaultValue: 'Loading...' })}
                        </span>
                      </div>
                    </div>
                  ) : error ? (
                    <div className="alert alert-danger" role="alert">
                      {error}
                    </div>
                  ) : selectedService && selectedSubtitle ? (
                    <div
                      className={`tab-pane fade show active`}
                      id={`pills-${selectedService.title.en.toLowerCase()}`}
                      role="tabpanel"
                      aria-labelledby={`pills-${selectedService.title.en.toLowerCase()}-tab`}
                    >
                      <div className="company_formation">
                        <div className="main_sides">
                          <div className="left_side_content">
                            <h5>{selectedSubtitle}</h5>
                            <p>
                              {selectedService.description[currentLanguage] ||
                                selectedService.description.en}
                            </p>
                            <div className="down_arrow">
                              <img
                                src={downArrow}
                                alt={t('images.down_arrow_alt', {
                                  defaultValue: 'Down Arrow',
                                })}
                              />
                            </div>
                          </div>
                          <div className="right_side_image mt-4 mt-md-0">
                            <img
                              src={selectedService.image || passportDetailed}
                              alt={selectedSubtitle}
                              onError={(e) =>
                                (e.currentTarget.src = passportDetailed)
                              }
                              style={{
                                width: '100%',
                                height: '250px',
                                borderRadius: '20px',
                                objectFit: 'cover',
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <p>
                      {t('errors.no_data', {
                        defaultValue:
                          'No data available for the selected subtitle.',
                      })}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="formation_section">
        <div className="container">
          <div className="row">
            <div className="col-md-12">
              <div className="main-title">
                <h4>
                  {t('formation.title', { defaultValue: 'Formation Process' })}
                </h4>
              </div>

              <div className="steps blue_icons">
                {[
                  process1,
                  process2,
                  process3,
                  process4,
                  process5,
                  process6,
                  process7,
                  process8,
                  process9,
                ].map((icon, index) => {
                  const stepNumber = index + 1;
                  const isActive = activeStep === stepNumber;
                  const isCompleted = stepNumber < activeStep;

                  return (
                    <div
                      key={index}
                      className={`step_no step_no_${stepNumber} ${isActive ? 'active' : isCompleted ? 'completed' : ''}`}
                      onClick={() => handleStepClick(stepNumber)}
                      data-step={stepNumber}
                      role="button"
                      aria-label={t(`formation.steps.${stepNumber}.title`, {
                        defaultValue: `Step ${stepNumber}`,
                      })}
                    >
                      <img
                        src={icon}
                        alt={t(`formation.steps.${stepNumber}.alt`, {
                          defaultValue: `Step ${stepNumber} Icon`,
                        })}
                      />
                      <div
                        className={`step_active_inactive ${isActive || isCompleted ? 'active' : ''}`}
                      ></div>
                    </div>
                  );
                })}
              </div>
              <div
                className="step-content mt-4 p-4"
                style={{
                  backgroundColor: '#082341',
                  color: '#fff',
                  lineHeight: '1.8',
                  maxWidth: '700px',
                }}
              >
                <h5 style={{ fontWeight: '600', marginBottom: '16px' }}>
                  {t(`formation.steps.${activeStep}.title`, {
                    defaultValue: `Step ${activeStep}`,
                  })}
                </h5>

                <ul
                  style={{
                    listStyle: 'none',
                    paddingLeft: '0',
                    marginBottom: '0',
                  }}
                >
                  <li style={{ position: 'relative', marginBottom: '10px' }}>
                    <span
                      style={{
                        width: '14px',
                        height: '14px',
                        border: '3px solid #fff',
                        borderRadius: '50%',
                        display: 'inline-block',
                        marginRight: '10px',
                      }}
                    ></span>
                    {t(`formation.steps.${activeStep}.description1`, {
                      defaultValue: 'Description not available.',
                    })}
                  </li>
                  {t(`formation.steps.${activeStep}.description2`, {
                    defaultValue: '',
                  }) && (
                    <li style={{ position: 'relative', marginBottom: '10px' }}>
                      <span
                        style={{
                          width: '14px',
                          height: '14px',
                          border: '3px solid #fff',
                          borderRadius: '50%',
                          display: 'inline-block',
                          marginRight: '10px',
                        }}
                      ></span>
                      {t(`formation.steps.${activeStep}.description2`)}
                    </li>
                  )}
                  {t(`formation.steps.${activeStep}.description3`, {
                    defaultValue: '',
                  }) && (
                    <li style={{ position: 'relative', marginBottom: '10px' }}>
                      <span
                        style={{
                          width: '14px',
                          height: '14px',
                          border: '3px solid #fff',
                          borderRadius: '50%',
                          display: 'inline-block',
                          marginRight: '10px',
                        }}
                      ></span>
                      {t(`formation.steps.${activeStep}.description3`)}
                    </li>
                  )}
                  {t(`formation.steps.${activeStep}.description4`, {
                    defaultValue: '',
                  }) && (
                    <li style={{ position: 'relative', marginBottom: '10px' }}>
                      <span
                        style={{
                          width: '14px',
                          height: '14px',
                          border: '3px solid #fff',
                          borderRadius: '50%',
                          display: 'inline-block',
                          marginRight: '10px',
                        }}
                      ></span>
                      {t(`formation.steps.${activeStep}.description4`)}
                    </li>
                  )}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
      <style>
        {`@media (max-width: 767px) {
        .services .nav-pills .nav-link{
        width: 300px;}
        }
        `}
      </style>
    </>
  );
};

export default Service;
